import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
} from '@tanstack/react-query';
import { MutationFetchFunction } from 'common/helpers/common';
import { ApiError, ApiResult } from 'common/repositories/common.model';

import {
  CruiseSaleModel,
  CruiseSaleMutationInput,
  CruiseSaleDeleteInput,
  CruiseSaleUpdateInput,
  CruiseSaleReschedulePutInput,
} from './model';

export function useCreateCruiseSale(
  options?: UseMutationOptions<
    ApiResult<CruiseSaleModel>,
    ApiError,
    CruiseSaleMutationInput
  >,
): UseMutationResult<
  ApiResult<CruiseSaleModel>,
  ApiError,
  CruiseSaleMutationInput
> {
  return useMutation<
    ApiResult<CruiseSaleModel>,
    ApiError,
    CruiseSaleMutationInput
  >(async function (body) {
    return await MutationFetchFunction({
      url: 'cruise-sales',
      method: 'POST',
      classType: CruiseSaleModel,
      body,
    });
  }, options);
}

export function useUpdateCruiseSale(
  options?: UseMutationOptions<
    ApiResult<CruiseSaleModel>,
    ApiError,
    CruiseSaleUpdateInput
  >,
): UseMutationResult<
  ApiResult<CruiseSaleModel>,
  ApiError,
  CruiseSaleUpdateInput
> {
  return useMutation<
    ApiResult<CruiseSaleModel>,
    ApiError,
    CruiseSaleUpdateInput
  >(async function (body) {
    return await MutationFetchFunction({
      url: `cruise-sales/${body.cruiseSaleId}`,
      method: 'PUT',
      classType: CruiseSaleModel,
      body: body.body,
    });
  }, options);
}

export function useRescheduleCruiseSale(
  options?: UseMutationOptions<
    ApiResult<CruiseSaleModel>,
    ApiError,
    CruiseSaleReschedulePutInput
  >,
): UseMutationResult<
  ApiResult<CruiseSaleModel>,
  ApiError,
  CruiseSaleReschedulePutInput
> {
  return useMutation<
    ApiResult<CruiseSaleModel>,
    ApiError,
    CruiseSaleReschedulePutInput
  >(async function (body) {
    return await MutationFetchFunction({
      url: `cruise-sales/${body.cruiseSaleId}/reschedule`,
      method: 'PUT',
      body: body.body,
    });
  }, options);
}

export function useDeleteCruiseSale(
  options?: UseMutationOptions<
    { message: string },
    ApiError,
    CruiseSaleDeleteInput
  >,
): UseMutationResult<{ message: string }, ApiError, CruiseSaleDeleteInput> {
  return useMutation<{ message: string }, ApiError, CruiseSaleDeleteInput>(
    async function (body) {
      return await MutationFetchFunction({
        url: `cruise-sales/${body.id}`,
        method: 'DELETE',
      });
    },
    options,
  );
}

export function useVoidCruiseSale(
  options?: UseMutationOptions<
    { message: string },
    ApiError,
    CruiseSaleDeleteInput
  >,
): UseMutationResult<{ message: string }, ApiError, CruiseSaleDeleteInput> {
  return useMutation<{ message: string }, ApiError, CruiseSaleDeleteInput>(
    async function (body) {
      return await MutationFetchFunction({
        url: `cruise-sales/${body.id}/void`,
        method: 'PATCH',
      });
    },
    options,
  );
}

export function useCompleteCruiseSale(
  options?: UseMutationOptions<
    { message: string },
    ApiError,
    CruiseSaleDeleteInput
  >,
): UseMutationResult<{ message: string }, ApiError, CruiseSaleDeleteInput> {
  return useMutation<{ message: string }, ApiError, CruiseSaleDeleteInput>(
    async function (body) {
      return await MutationFetchFunction({
        url: `cruise-sales/${body.id}/complete`,
        method: 'PATCH',
      });
    },
    options,
  );
}

export function useRevertCompleteCruiseSale(
  options?: UseMutationOptions<
    { message: string },
    ApiError,
    CruiseSaleDeleteInput
  >,
): UseMutationResult<{ message: string }, ApiError, CruiseSaleDeleteInput> {
  return useMutation<{ message: string }, ApiError, CruiseSaleDeleteInput>(
    async function (body) {
      return await MutationFetchFunction({
        url: `cruise-sales/${body.id}/revert-complete`,
        method: 'PATCH',
      });
    },
    options,
  );
}
